/**
 * Store-dashboard sync health from the same sources as IntegrationDetailPage:
 * `productSyncSelections` size + the latest completed integration run.
 *
 * Do not use catalog facet totals or a MAP-audit `mappingsChecked` here — those
 * are a different population (whole brand catalog / last audit slice) and will
 * not match "Products in selection" on the Shopify sync log.
 */

const BENIGN_SKIP_REASONS = new Set([
  'No changes since last sync',
  'Already up to date in Shopify (no changes to push)',
  // Emitted for unchanged Shopify listings (shopifyService.ts) and already treated
  // as a benign skip by the ERP audit (erpSyncAuditService.ts) — not a blocked product.
  'No differences detected',
]);

export function isBenignSyncSkipReason(reason: string): boolean {
  return BENIGN_SKIP_REASONS.has(reason);
}

export interface StoreSyncRunLike {
  status?: string;
  productsTotal?: number;
  productsProcessed?: number;
  productsCreated?: number;
  productsUpdated?: number;
  productsSkipped?: number;
  errorCount?: number;
  warningCount?: number;
  skipReasons?: Record<string, number>;
}

export interface StoreSyncHealth {
  selected: number;
  live: number;
  blocked: number;
  /**
   * Selected products the latest completed run never reached — added after it
   * started, or nothing has run yet. They push on the next scheduled run, so
   * they are neither live nor blocked.
   */
  pending: number;
  /** Products the latest completed run covered (its `productsTotal`, else its outcome sum). */
  lastRunCovered: number;
  /** True when we have a selection count or a completed run to read. */
  available: boolean;
}

const TERMINAL_STATUSES = new Set(['success', 'partial', 'completed', 'error']);

export function pickLatestCompletedSyncRun<T extends { status?: string }>(runs: T[]): T | null {
  return runs.find((run) => TERMINAL_STATUSES.has(String(run.status || ''))) || null;
}

export function deriveStoreSyncHealth(input: {
  selectionProductCount?: number | null;
  latestCompletedRun?: StoreSyncRunLike | null;
}): StoreSyncHealth {
  const selected = Math.max(0, Math.floor(Number(input.selectionProductCount) || 0));
  const run = input.latestCompletedRun;
  if (!run) {
    if (selected === 0) return { selected: 0, live: 0, blocked: 0, pending: 0, lastRunCovered: 0, available: false };
    // Nothing has run yet: the whole selection is waiting on the first sync, not failing.
    return { selected, live: 0, blocked: 0, pending: selected, lastRunCovered: 0, available: true };
  }

  const created = Math.max(0, Math.floor(Number(run.productsCreated) || 0));
  const updated = Math.max(0, Math.floor(Number(run.productsUpdated) || 0));
  const skipped = Math.max(0, Math.floor(Number(run.productsSkipped) || 0));
  const errors = Math.max(0, Math.floor(Number(run.errorCount) || 0));

  let blockedFromSkips = 0;
  const reasons = run.skipReasons;
  if (reasons && typeof reasons === 'object') {
    for (const [reason, count] of Object.entries(reasons)) {
      const n = Math.max(0, Math.floor(Number(count) || 0));
      if (!isBenignSyncSkipReason(reason)) blockedFromSkips += n;
    }
    // The backend reconciles inflated outcome counters after duplicate chunk
    // processing, but incrementally accumulated skipReasons can still exceed the
    // reconciled skipped count — don't let the sum overstate blocked products.
    blockedFromSkips = Math.min(blockedFromSkips, skipped);
  }
  // Runs without skipReasons (legacy, pre-tracking) can't be attributed here:
  // warningCount also includes warnings on successfully synced products (e.g.
  // pre-order/taxonomy warnings), so it is not a reliable stand-in for blocked
  // skips. The sync log itself shows no breakdown for these runs either.

  const blockedRaw = blockedFromSkips + errors;
  const outcomeSum = created + updated + skipped + errors;
  // What the run actually walked. A store that adds products after a run has a
  // selection larger than this; those products are pending, not live.
  const lastRunCovered = Math.max(0, Math.floor(Number(run.productsTotal) || 0) || outcomeSum);
  const liveFromOutcomes = created + updated + Math.max(0, skipped - blockedFromSkips);
  const selectedOut = selected > 0 ? selected : lastRunCovered;
  const pending = selected > 0 ? Math.max(0, selected - lastRunCovered) : 0;
  const synced = selectedOut - pending;
  const blocked = Math.min(synced, blockedRaw);
  const live = Math.min(synced, Math.max(0, selected > 0 ? synced - blocked : liveFromOutcomes));

  return { selected: selectedOut, live, blocked, pending, lastRunCovered, available: true };
}
